'use client';

import { useQuery } from '@tanstack/react-query';
import { useWindowVirtualizer } from '@tanstack/react-virtual';
import {
    Fragment,
    memo,
    ReactNode,
    useCallback,
    useContext,
    useDeferredValue,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
    useSyncExternalStore,
} from 'react';
import type { StorefrontCategory } from '@/api/menu-list/storefront-menu-snapshot.types';
import { useStorefrontItemsIndex } from '@/entities/product/lib/useStorefrontMenu';
import { QueryApi } from '@/features/query/api';
import { useStorageValue } from '@/shared/lib/storage';
import type { MediaStyles } from '@/shared/ui/layout';
import { Layout } from '@/shared/ui/layout';
import { CategoriesContext, CategoriesProvider } from '@/shared/ui/providers/categories-provider';
import { CategoryItemsConfigProvider } from '@/shared/ui/providers/category-items-config-provider';
import { CategoryProvider } from '@/shared/ui/providers/category-provider';
import { MenuItemData, MenuItemProvider } from '@/shared/ui/providers/menu-item-provider';
import {
    buildMenuFeedRows,
    FeedRow,
    FeedScrollAnchor,
    findFeedRowIndexForAnchor,
} from './build-menu-feed-rows';
import {
    getCategoryFilterScrollY,
    isSameCategorySelection,
    MENU_CATEGORY_PICKER_ID,
    shouldScrollToCategoryFilter,
    shouldSkipRemasureForCategoryFilter,
    type CategorySelection,
} from './category-filter-scroll';
import { collectFeedSizes } from './collect-feed-sizes';
import { normalizeId, resolveInjections, resolveOverride } from './helpers';
import { isViewportChromeResize } from './is-viewport-chrome-resize';
import { CategoriesFilters, CategoryOverrideConfig, InjectionConfig, ItemsConfig } from './types';

/** Match Layout mediaQueryMap so column count tracks itemsWrapper CSS breakpoints. */
const MD_MIN = 720;
const LG_MIN = 910;
const RESIZE_DEBOUNCE_MS = 120;
// ponytail: fallback only — real sizes come from premeasure; keep ≥ typical mobile row.
const ESTIMATE_HEADER = 72;
const ESTIMATE_ITEMS = 400;

type Props = {
    filters?: CategoriesFilters;
    categoryHeader?: ReactNode;
    itemTemplate?: ReactNode;
    categoryWrapper?: { media?: MediaStyles };
    itemsWrapper?: { media?: MediaStyles };
    injections?: InjectionConfig[];
    overrides?: CategoryOverrideConfig[];
    items?: ItemsConfig;
};

const flattenCategories = (categories: StorefrontCategory[]) => {
    const flat: StorefrontCategory[] = [];
    const walk = (category: StorefrontCategory) => {
        flat.push(category);
        category.children?.forEach(walk);
    };
    categories.forEach(walk);
    return flat;
};

const hasItems = (category: StorefrontCategory): boolean => {
    if (category.itemHashes.length > 0) return true;
    return category.children?.some(hasItems) ?? false;
};

const getCategoryWithSubcategories = (
    categories: StorefrontCategory[],
    categoryId: string,
): StorefrontCategory[] => {
    const flatCategories = flattenCategories(categories);
    const category = flatCategories.find((cat) => cat.id === categoryId);
    if (!category) return [];

    const result: StorefrontCategory[] = [category];
    const addSubcategories = (cat: StorefrontCategory) => {
        if (cat.children) {
            [...cat.children]
                .sort((a, b) => (a.sortIndex ?? 0) - (b.sortIndex ?? 0))
                .forEach((child) => {
                    result.push(child);
                    addSubcategories(child);
                });
        }
    };
    addSubcategories(category);
    return result;
};

const resolveCategoryOverride = (
    overrides: CategoryOverrideConfig[] | undefined,
    id: string,
    index: number,
) => {
    if (!overrides?.length) return undefined;
    const byId = overrides.find((override) => override.id && override.id === id);
    if (byId) return byId;
    return overrides.find((override) => typeof override.index === 'number' && override.index === index);
};

const resolveCategoryInjections = (
    injections: InjectionConfig[] | undefined,
    id: string,
    index: number,
    position: 'before' | 'after',
) => {
    if (!injections?.length) return [];
    return injections
        .filter(Boolean)
        .filter((injection) => (injection.position ?? 'after') === position)
        .filter((injection) => {
            if (injection.id) return injection.id === id;
            if (typeof injection.index === 'number') return injection.index === index;
            return false;
        })
        .map((injection, injectionIndex) => (
            <Fragment key={`${position}-${id}-${index}-${injectionIndex}`}>{injection.component}</Fragment>
        ));
};

const mergeItemsConfig = (base?: ItemsConfig, override?: ItemsConfig): ItemsConfig => ({
    limit: override && 'limit' in override ? override.limit : base?.limit,
    injections: override?.injections ?? base?.injections,
    overrides: override?.overrides ?? base?.overrides,
});

const resolveMenuListId = (
    category: StorefrontCategory,
    menuLists?: { id?: string; categories?: StorefrontCategory[] }[],
) => {
    if (!menuLists) return null;
    for (const menuList of menuLists) {
        if (!menuList.categories) continue;
        const stack = [...menuList.categories];
        while (stack.length > 0) {
            const current = stack.pop();
            if (!current) continue;
            if (current.id === category.id) return menuList.id ?? null;
            if (current.children?.length) stack.push(...current.children);
        }
    }
    return null;
};

const getCategoryItems = (
    category: StorefrontCategory,
    menuListId: string | null,
    itemsLookup: ReturnType<typeof useStorefrontItemsIndex>,
): MenuItemData[] =>
    category.itemHashes.flatMap((hash, sortIndex) => {
        const entry = itemsLookup[hash];
        if (!entry?.placement.slug) return [];
        return [
            {
                hash,
                slug: entry.placement.slug,
                itemId: entry.body.id,
                item: entry.body,
                menuListId,
                categoryId: category.id,
                sortIndex,
                overrideItem: entry.placement.overridePrices?.length
                    ? { prices: entry.placement.overridePrices }
                    : null,
            },
        ];
    });

const readColumnCount = () => {
    if (typeof window === 'undefined') return 2;
    if (window.matchMedia(`(min-width: ${LG_MIN}px)`).matches) return 4;
    if (window.matchMedia(`(min-width: ${MD_MIN}px)`).matches) return 3;
    return 2;
};

const subscribeColumnCount = (onStoreChange: () => void) => {
    const md = window.matchMedia(`(min-width: ${MD_MIN}px)`);
    const lg = window.matchMedia(`(min-width: ${LG_MIN}px)`);
    md.addEventListener('change', onStoreChange);
    lg.addEventListener('change', onStoreChange);
    return () => {
        md.removeEventListener('change', onStoreChange);
        lg.removeEventListener('change', onStoreChange);
    };
};

/** Client snapshot is sync (no post-paint flash). Server/hydration snapshot stays 2 for SEO match. */
const useMenuColumnCount = () => useSyncExternalStore(subscribeColumnCount, readColumnCount, () => 2);

type RowRenderProps = {
    row: FeedRow;
    categoryHeader?: ReactNode;
    itemTemplate?: ReactNode;
    categoryWrapperMedia?: MediaStyles;
    itemsGap: string | number;
    columnCount: number;
    itemsLookup: ReturnType<typeof useStorefrontItemsIndex>;
    categoryInjections?: InjectionConfig[];
    categoryOverrides?: CategoryOverrideConfig[];
    itemsConfigBase?: ItemsConfig;
};

const FeedMenuItem = memo(function FeedMenuItem({
    menuItem,
    index,
    itemsLookup,
    children,
}: {
    menuItem: MenuItemData;
    index: number;
    itemsLookup: ReturnType<typeof useStorefrontItemsIndex>;
    children: ReactNode;
}) {
    const value = useMemo(
        () => ({
            data: menuItem,
            index,
            itemsIndex: itemsLookup,
        }),
        [menuItem, index, itemsLookup],
    );

    return <MenuItemProvider value={value}>{children}</MenuItemProvider>;
});

const FeedRowView = memo(function FeedRowView({
    row,
    categoryHeader,
    itemTemplate,
    categoryWrapperMedia,
    itemsGap,
    columnCount,
    itemsLookup,
    categoryInjections,
    categoryOverrides,
    itemsConfigBase,
}: RowRenderProps) {
    const override = resolveCategoryOverride(categoryOverrides, row.category.id, row.categoryIndex);
    const itemsConfig = useMemo(
        () => mergeItemsConfig(itemsConfigBase, override?.items),
        [itemsConfigBase, override?.items],
    );
    const headerContent = override?.children ?? categoryHeader;
    const before =
        row.kind === 'header'
            ? resolveCategoryInjections(categoryInjections, row.category.id, row.categoryIndex, 'before')
            : [];
    const after =
        row.kind === 'items' && row.isLastInCategory
            ? resolveCategoryInjections(categoryInjections, row.category.id, row.categoryIndex, 'after')
            : [];

    const categoryMarginBottom =
        (categoryWrapperMedia?.initial?.marginBottom as string | number | undefined) ?? '1.5rem';
    const categoryGap = (categoryWrapperMedia?.initial?.gap as string | number | undefined) ?? '1rem';

    const categoryValue = useMemo(() => ({ data: row.category }), [row.category]);

    const gridStyle = useMemo(
        () =>
            row.kind === 'items'
                ? {
                      display: 'grid' as const,
                      gridTemplateColumns: `repeat(${columnCount}, minmax(0, 1fr))`,
                      gap: itemsGap,
                      marginBottom: row.isLastInCategory ? categoryMarginBottom : itemsGap,
                  }
                : undefined,
        // ponytail: row.isLastInCategory only exists on items rows; kind gate keeps it safe
        [row, columnCount, itemsGap, categoryMarginBottom],
    );

    return (
        <CategoryProvider value={categoryValue}>
            <CategoryItemsConfigProvider value={itemsConfig}>
                {row.kind === 'header' ? (
                    <div style={{ marginBottom: categoryGap }}>
                        {before}
                        {headerContent}
                    </div>
                ) : (
                    <div style={gridStyle}>
                        {row.items.flatMap((menuItem, chunkIndex) => {
                            const index = row.startIndex + chunkIndex;
                            const itemId = normalizeId(menuItem.itemId);
                            const hash = normalizeId(menuItem.hash);
                            const itemOverride = resolveOverride(itemsConfig.overrides, itemId, hash, index);
                            const itemChildren = itemOverride?.children ?? itemTemplate;
                            const itemBefore = resolveInjections(
                                itemsConfig.injections,
                                itemId,
                                hash,
                                index,
                                'before',
                            );
                            const itemAfter = resolveInjections(
                                itemsConfig.injections,
                                itemId,
                                hash,
                                index,
                                'after',
                            );

                            return [
                                ...itemBefore,
                                <FeedMenuItem
                                    key={menuItem.hash ?? itemId ?? `${index}`}
                                    menuItem={menuItem}
                                    index={index}
                                    itemsLookup={itemsLookup}
                                >
                                    {itemChildren}
                                </FeedMenuItem>,
                                ...itemAfter,
                            ];
                        })}
                        {after}
                    </div>
                )}
            </CategoryItemsConfigProvider>
        </CategoryProvider>
    );
});

type VirtualRowProps = RowRenderProps & {
    index: number;
    start: number;
    scrollMargin: number;
    measureElement: (node: Element | null) => void;
};

/** Outer shell still updates transform on scroll; memo skips when start/row unchanged. */
const VirtualRow = memo(function VirtualRow({
    index,
    start,
    scrollMargin,
    measureElement,
    ...rowProps
}: VirtualRowProps) {
    return (
        <div
            data-index={index}
            ref={measureElement}
            style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                transform: `translateY(${start - scrollMargin}px)`,
            }}
        >
            <FeedRowView {...rowProps} />
        </div>
    );
});

const VirtualizedMenuCategoriesFeedContent = ({
    filters,
    categoryHeader,
    itemTemplate,
    categoryWrapper,
    itemsWrapper,
    injections,
    overrides,
    items,
}: Props) => {
    const { data } = useContext(CategoriesContext);
    const menuListsQuery = useQuery(QueryApi.MenuList.queries.MenuLists);
    const itemsLookup = useStorefrontItemsIndex();
    const columnCount = useMenuColumnCount();

    const [categoryIdRaw] = useStorageValue(filters?.categoryId);
    const [subCategoryIdRaw] = useStorageValue(filters?.subCategoryId);
    const categoryId = useDeferredValue(categoryIdRaw);
    const subCategoryId = useDeferredValue(subCategoryIdRaw);

    // First paint uses CSS breakpoints (Layout). JS columnCount hydrates as 2 — don't
    // measure/virtualize until after mount when getSnapshot has the real client value.
    const [columnsReady, setColumnsReady] = useState(false);
    const [virtualize, setVirtualize] = useState(false);
    const [scrollMargin, setScrollMargin] = useState(0);
    const listRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        setColumnsReady(true);
    }, []);

    const filteredCategories = useMemo(() => {
        let result = filters?.includeSubcategories ? flattenCategories(data) : data;

        if (categoryId) {
            result = getCategoryWithSubcategories(data, categoryId);
        }

        if (subCategoryId) {
            const flatCategories = flattenCategories(data);
            result = flatCategories.filter((category) => category.id === subCategoryId);
        }

        if (filters?.onlyWithItems) {
            result = result.filter((category) => hasItems(category));
        }

        return result;
    }, [data, categoryId, subCategoryId, filters?.includeSubcategories, filters?.onlyWithItems]);

    const categoryEntries = useMemo(() => {
        const menuLists = menuListsQuery.data?.menuLists;
        return filteredCategories.map((category, categoryIndex) => {
            const override = resolveCategoryOverride(overrides, category.id, categoryIndex);
            const itemsConfig = mergeItemsConfig(items, override?.items);
            const menuListId = resolveMenuListId(category, menuLists);
            let resolved = getCategoryItems(category, menuListId, itemsLookup);
            if (typeof itemsConfig.limit === 'number') {
                resolved = resolved.slice(0, Math.max(0, itemsConfig.limit));
            }
            return { category, items: resolved };
        });
    }, [filteredCategories, menuListsQuery.data?.menuLists, itemsLookup, overrides, items]);

    const rows = useMemo(
        () => buildMenuFeedRows(categoryEntries, columnCount),
        [categoryEntries, columnCount],
    );
    const rowsKey = useMemo(() => rows.map((row) => row.key).join('\0'), [rows]);

    const rowsRef = useRef(rows);
    rowsRef.current = rows;
    const pendingAnchorRef = useRef<FeedScrollAnchor | null>(null);
    const pendingScrollToStartRef = useRef(false);
    const categorySelectionRef = useRef<CategorySelection>({ categoryId, subCategoryId });
    const columnCountRef = useRef(columnCount);
    const virtualizerRef = useRef<ReturnType<typeof useWindowVirtualizer> | null>(null);
    const sizeByKeyRef = useRef(new Map<string, number>());
    const rowsKeyWhileVirtualRef = useRef(rowsKey);

    const itemsGap = (itemsWrapper?.media?.initial?.gap as string | number | undefined) ?? '1rem';

    const captureAnchor = useCallback((): FeedScrollAnchor | null => {
        const virtualizer = virtualizerRef.current;
        const currentRows = rowsRef.current;
        if (!virtualizer || currentRows.length === 0) return null;

        const scrollY = window.scrollY;
        const virtualItems = virtualizer.getVirtualItems();
        for (const vItem of virtualItems) {
            const row = currentRows[vItem.index];
            if (!row) continue;
            if (vItem.end <= scrollY) continue;
            const offsetWithinRow = Math.max(0, scrollY - vItem.start);
            if (row.kind === 'items' && row.items[0]) {
                return { kind: 'item', id: row.items[0].hash, offsetWithinRow };
            }
            if (row.kind === 'header') {
                return { kind: 'header', id: row.category.id, offsetWithinRow };
            }
        }
        return null;
    }, []);

    const restoreAnchor = useCallback((anchor: FeedScrollAnchor | null) => {
        const instance = virtualizerRef.current;
        if (!instance || !anchor) return;
        const index = findFeedRowIndexForAnchor(rowsRef.current, anchor);
        if (index === null) return;

        instance.measure();
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                const offset = instance.getOffsetForIndex(index, 'start');
                if (!offset) return;
                instance.scrollToOffset(offset[0] + anchor.offsetWithinRow);
            });
        });
    }, []);

    // Full list (same row model as virtual) → measure → virtualize.
    // Wait for columnsReady so we never premeasure the hydration columnCount=2 snapshot.
    useLayoutEffect(() => {
        if (!columnsReady) return;

        if (virtualize) {
            if (rowsKeyWhileVirtualRef.current !== rowsKey) {
                rowsKeyWhileVirtualRef.current = rowsKey;
                const nextSelection = { categoryId, subCategoryId };
                const selectionChanged = !isSameCategorySelection(
                    categorySelectionRef.current,
                    nextSelection,
                );
                categorySelectionRef.current = nextSelection;
                if (shouldSkipRemasureForCategoryFilter(selectionChanged)) {
                    pendingAnchorRef.current = null;
                    pendingScrollToStartRef.current = true;
                    // Filter only changes which rows exist — remasure would paint the full list (flash).
                    return;
                }
                pendingAnchorRef.current = captureAnchor();
                // Defer — setState here can re-enter TanStack notify/flushSync mid-layout.
                queueMicrotask(() => setVirtualize(false));
            }
            return;
        }

        const root = listRef.current;
        if (!root) return;
        sizeByKeyRef.current = collectFeedSizes(root);
        setScrollMargin(root.offsetTop);
        rowsKeyWhileVirtualRef.current = rowsKey;
        columnCountRef.current = columnCount;
        categorySelectionRef.current = { categoryId, subCategoryId };
        setVirtualize(true);
    }, [columnsReady, virtualize, rowsKey, columnCount, categoryId, subCategoryId, captureAnchor]);

    // After the filtered rows paint: scroll only when a filter is active and the page is below the target.
    useEffect(() => {
        if (!pendingScrollToStartRef.current) return;
        pendingScrollToStartRef.current = false;
        const feed = listRef.current;
        if (!feed) return;
        const picker = document.getElementById(MENU_CATEGORY_PICKER_ID);
        const top = getCategoryFilterScrollY(feed, picker);
        if (shouldScrollToCategoryFilter(categorySelectionRef.current, window.scrollY, top)) {
            window.scrollTo({ top });
        }
    }, [rowsKey]);

    // After virtualize flips on, restore scroll outside layout (TanStack measure → flushSync).
    useEffect(() => {
        if (!virtualize || !pendingAnchorRef.current) return;
        const anchor = pendingAnchorRef.current;
        pendingAnchorRef.current = null;
        restoreAnchor(anchor);
    }, [virtualize, restoreAnchor]);

    // List offset can change when chrome above reflows (fonts, banners).
    useLayoutEffect(() => {
        if (!listRef.current || !virtualize) return;
        const top = listRef.current.offsetTop;
        if (top !== scrollMargin) setScrollMargin(top);
    }, [rows.length, columnCount, scrollMargin, virtualize]);

    const virtualizer = useWindowVirtualizer({
        count: rows.length,
        estimateSize: (index) => {
            const row = rows[index];
            if (!row) return ESTIMATE_ITEMS;
            return (
                sizeByKeyRef.current.get(row.key) ??
                (row.kind === 'header' ? ESTIMATE_HEADER : ESTIMATE_ITEMS)
            );
        },
        overscan: 4,
        scrollMargin,
        getItemKey: (index) => rows[index]?.key ?? index,
        enabled: virtualize,
        // Resize remasure runs around React layout; sync flushSync throws mid-lifecycle.
        useFlushSync: false,
    });
    virtualizerRef.current = virtualizer;

    // Width change (not browser chrome): remasure via full list, then restore scroll.
    useEffect(() => {
        if (!virtualize) return;
        let lastW = window.innerWidth;
        let lastH = window.innerHeight;
        let timer: ReturnType<typeof setTimeout> | undefined;
        const onResize = () => {
            const w = window.innerWidth;
            const h = window.innerHeight;
            if (isViewportChromeResize(lastW, lastH, w, h)) {
                lastH = h;
                return;
            }
            lastW = w;
            lastH = h;

            if (!pendingAnchorRef.current) {
                pendingAnchorRef.current = captureAnchor();
            }
            clearTimeout(timer);
            timer = setTimeout(() => {
                if (columnCountRef.current !== readColumnCount()) {
                    // Breakpoint change rebuilds rows → rowsKey effect remasures.
                    return;
                }
                setVirtualize(false);
            }, RESIZE_DEBOUNCE_MS);
        };
        window.addEventListener('resize', onResize);
        return () => {
            clearTimeout(timer);
            window.removeEventListener('resize', onResize);
        };
    }, [virtualize, captureAnchor]);

    // Before column matchMedia updates state, stash anchor from the resize that crossed a breakpoint.
    useEffect(() => {
        if (!virtualize) return;
        const md = window.matchMedia(`(min-width: ${MD_MIN}px)`);
        const lg = window.matchMedia(`(min-width: ${LG_MIN}px)`);
        const stash = () => {
            pendingAnchorRef.current = captureAnchor();
        };
        md.addEventListener('change', stash);
        lg.addEventListener('change', stash);
        return () => {
            md.removeEventListener('change', stash);
            lg.removeEventListener('change', stash);
        };
    }, [virtualize, captureAnchor]);

    const rowSharedProps = {
        categoryHeader,
        itemTemplate,
        categoryWrapperMedia: categoryWrapper?.media,
        itemsGap,
        columnCount,
        itemsLookup,
        categoryInjections: injections,
        categoryOverrides: overrides,
        itemsConfigBase: items,
    };

    const renderCssCategory = (entry: (typeof categoryEntries)[number], categoryIndex: number) => {
        const override = resolveCategoryOverride(overrides, entry.category.id, categoryIndex);
        const itemsConfig = mergeItemsConfig(items, override?.items);
        const headerContent = override?.children ?? categoryHeader;
        const before = resolveCategoryInjections(injections, entry.category.id, categoryIndex, 'before');
        const after = resolveCategoryInjections(injections, entry.category.id, categoryIndex, 'after');
        const categoryMarginBottom =
            (categoryWrapper?.media?.initial?.marginBottom as string | number | undefined) ?? '1.5rem';
        const categoryGap = (categoryWrapper?.media?.initial?.gap as string | number | undefined) ?? '1rem';

        return (
            <CategoryProvider key={entry.category.id} value={{ data: entry.category }}>
                <CategoryItemsConfigProvider value={itemsConfig}>
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: categoryGap,
                            marginBottom: categoryMarginBottom,
                        }}
                    >
                        <div>
                            {before}
                            {headerContent}
                        </div>
                        <Layout
                            media={
                                itemsWrapper?.media ?? {
                                    initial: {
                                        display: 'grid',
                                        gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                                        gap: '1rem',
                                    },
                                    md: { gridTemplateColumns: 'repeat(3, minmax(0, 1fr))' },
                                    lg: { gridTemplateColumns: 'repeat(4, minmax(0, 1fr))' },
                                }
                            }
                        >
                            {entry.items.flatMap((menuItem, index) => {
                                const itemId = normalizeId(menuItem.itemId);
                                const hash = normalizeId(menuItem.hash);
                                const itemOverride = resolveOverride(
                                    itemsConfig.overrides,
                                    itemId,
                                    hash,
                                    index,
                                );
                                const itemChildren = itemOverride?.children ?? itemTemplate;
                                const itemBefore = resolveInjections(
                                    itemsConfig.injections,
                                    itemId,
                                    hash,
                                    index,
                                    'before',
                                );
                                const itemAfter = resolveInjections(
                                    itemsConfig.injections,
                                    itemId,
                                    hash,
                                    index,
                                    'after',
                                );

                                return [
                                    ...itemBefore,
                                    <MenuItemProvider
                                        key={menuItem.hash ?? itemId ?? `${index}`}
                                        value={{
                                            data: menuItem,
                                            index,
                                            itemsIndex: itemsLookup,
                                        }}
                                    >
                                        {itemChildren}
                                    </MenuItemProvider>,
                                    ...itemAfter,
                                ];
                            })}
                        </Layout>
                        {after}
                    </div>
                </CategoryItemsConfigProvider>
            </CategoryProvider>
        );
    };

    const virtualItems = virtualize ? virtualizer.getVirtualItems() : [];

    // Stable outer ref so scrollMargin stays valid across measure→virtual switch.
    return (
        <div ref={listRef} style={{ position: 'relative', width: '100%' }}>
            {!columnsReady ? (
                categoryEntries.map((entry, index) => renderCssCategory(entry, index))
            ) : !virtualize ? (
                rows.map((row) => (
                    <div key={row.key} data-feed-key={row.key}>
                        <FeedRowView row={row} {...rowSharedProps} />
                    </div>
                ))
            ) : (
                <div
                    style={{
                        height: virtualizer.getTotalSize(),
                        width: '100%',
                        position: 'relative',
                    }}
                >
                    {virtualItems.map((vItem) => {
                        const row = rows[vItem.index];
                        if (!row) return null;
                        return (
                            <VirtualRow
                                key={row.key}
                                index={vItem.index}
                                start={vItem.start}
                                scrollMargin={scrollMargin}
                                measureElement={virtualizer.measureElement}
                                row={row}
                                {...rowSharedProps}
                            />
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export const VirtualizedMenuCategoriesFeed = (props: Props) => {
    return (
        <CategoriesProvider>
            <VirtualizedMenuCategoriesFeedContent {...props} />
        </CategoriesProvider>
    );
};
